import React from 'react';
import { NavLink as RouterNavLink, Link as RouterLink, useLocation } from 'react-router-dom';
import { skipToken, useQuery } from '@tanstack/react-query';

import Logo from '@dashboard/components/Logo';
import { pageKey } from '@dashboard/lib/queryKeys';
import { TooltipArrow } from '@radix-ui/react-tooltip';
import { Tooltip, TooltipContent, TooltipTrigger } from '@dashboard/components/ui/tooltip';

import { Profile } from './Navigation';
import { MAIN_SECTIONS } from './mainSections';

const RAIL_ITEM_CLASS =
  'relative flex size-8 items-center justify-center rounded-md text-sidebar-foreground hover:bg-sidebar-accent [&>svg]:size-4 [&>svg]:shrink-0 pointer-coarse:after:absolute pointer-coarse:after:-inset-x-3 pointer-coarse:after:-inset-y-0.5';

const RAIL_ITEM_ACTIVE_CLASS = 'bg-sidebar-accent text-sidebar-accent-foreground';

const SECTION_BY_PAGE_TYPE = { event: '/events', form: '/forms' };

export const RAIL_WIDTH = '3.5rem';

// A page's screens live under /pages/:id, so its section comes from the page's type, read from
// the page query the editor already ran.
function usePageSection() {
  const { pathname } = useLocation();
  const pageId = pathname.match(/\/pages\/([^/]+)/)?.[1];
  const { data: page } = useQuery({ queryKey: pageKey(pageId), queryFn: skipToken });
  if (!pageId || !page?.type) return null;
  return SECTION_BY_PAGE_TYPE[page.type] ?? '/checkouts';
}

// The main dashboard's sections as icons beside a page's own sidebar.
export default function MainNavRail() {
  const pageSection = usePageSection();
  return (
    <nav
      aria-label="Dashboard"
      style={{ top: 'var(--top-banner-h, 0px)', width: RAIL_WIDTH }}
      className="fixed bottom-0 left-0 z-50 hidden flex-col border-r border-gray-200 dark:border-border bg-white dark:bg-background md:flex"
    >
      {/* Logo and icons sit where the main sidebar draws them, so nothing moves going into a page. */}
      <div className="flex h-14 shrink-0 items-center pl-4">
        <RouterLink
          to="/"
          aria-label="Dashboard home"
          className="flex items-center text-foreground"
        >
          <Logo size={14} />
        </RouterLink>
      </div>

      <ul className="flex flex-col gap-1 px-2 pt-4">
        {MAIN_SECTIONS.map(({ to, href, label, Icon, exact }) => (
          <li key={label}>
            <Tooltip>
              <TooltipTrigger asChild>
                {href ? (
                  <a
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={label}
                    className={RAIL_ITEM_CLASS}
                  >
                    <Icon />
                  </a>
                ) : (
                  <RouterNavLink
                    to={to}
                    exact={exact}
                    isActive={(match, location) =>
                      to === pageSection || (!!match && !location.pathname.startsWith('/pages/'))
                    }
                    aria-label={label}
                    className={RAIL_ITEM_CLASS}
                    activeClassName={RAIL_ITEM_ACTIVE_CLASS}
                  >
                    <Icon />
                  </RouterNavLink>
                )}
              </TooltipTrigger>
              <TooltipContent
                side="right"
                sideOffset={6}
                className="overflow-visible border-0 bg-gray-900 dark:bg-primary px-2 py-1 text-xs font-medium text-white dark:text-primary-foreground shadow-none"
              >
                {label}
                <TooltipArrow
                  width={10}
                  height={5}
                  className="fill-gray-900 dark:fill-foreground"
                />
              </TooltipContent>
            </Tooltip>
          </li>
        ))}
      </ul>

      {/* The avatar sits where the main sidebar draws it; its name and email stay hidden. */}
      <div className="mt-auto w-14 px-3 pb-3 [&_[data-sidebar=menu-button]]:size-8 [&_[data-sidebar=menu-button]]:p-0 [&_[data-sidebar=menu-button]>:not(:first-child)]:hidden">
        <Profile />
      </div>
    </nav>
  );
}
